import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { trackConversion } from '../utils/analytics';
import { 
  Zap, Brain, MessageSquare, Sparkles, ArrowRight, Clock
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Handle CTA clicks with tracking
  const handleCTAClick = (ctaName, location) => {
    trackConversion.ctaClicked(ctaName, location);
    navigate('/auth');
  };

  // Redirect logged-in users to chat
  useEffect(() => {
    if (!loading && user) {
      navigate('/chat', { replace: true });
    }
  }, [user, loading, navigate]);

  // Show nothing while checking auth to prevent flash
  if (loading) {
    return null;
  }

  const features = [
    {
      icon: Brain,
      title: 'Thinks Like You',
      description: 'Learns your role, goals, and communication style to provide personalized assistance.'
    },
    {
      icon: MessageSquare,
      title: 'Multiple Modes',
      description: 'Switch between General, Emails, Ideas, and Projects modes for specialized help.'
    },
    {
      icon: Sparkles,
      title: 'Remembers Context',
      description: 'Stores important facts and preferences so you never repeat yourself.'
    },
    {
      icon: Clock,
      title: 'Saves You Hours',
      description: 'Draft emails, plan projects, and brainstorm ideas in seconds, not hours.'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="relative inline-block">
            <div className="absolute -top-[6px] left-0">
              <div className="bg-white text-background px-[3px] py-[3px] rounded text-[7px] font-bold" style={{ fontFamily: "'Coco Gothic', sans-serif" }}>hey</div>
            </div>
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Coco Gothic', sans-serif" }}>arlo</span>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleCTAClick('Sign In', 'header')}
              className="hover:bg-emerald-500/10 hover:text-emerald-400"
            >
              Sign In
            </Button>
            <Button size="sm" onClick={() => handleCTAClick('Get Started', 'header')} className="bg-emerald-500 hover:bg-emerald-600">
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Your AI assistant that
              <span className="block text-emerald-400">actually knows you</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
              Arlo helps business professionals think, plan, write, and make decisions faster. 
              One assistant that learns your style and remembers what matters.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => handleCTAClick('Start Free', 'hero')} className="gap-2 bg-emerald-500 hover:bg-emerald-600">
                Start Free <ArrowRight className="h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => { trackConversion.ctaClicked('View Pricing', 'hero'); navigate('/pricing'); }}>
                View Pricing
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              No credit card required · 5 free messages daily
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 border-t border-border/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Built for how you work</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Not another generic chatbot. Arlo is designed specifically for busy professionals.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="p-6 bg-card border border-border rounded-xl"
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-4">
                  <feature.icon className="h-5 w-5 text-emerald-500" />
                </div>
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 border-t border-border/50 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">What can Arlo help with?</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              'Draft professional emails in your voice',
              'Plan and organize complex projects',
              'Brainstorm ideas and solutions',
              'Prepare for important meetings',
              'Write documents and proposals',
              'Make better decisions faster'
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-4 bg-card border border-border rounded-lg">
                <Zap className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Ready to work smarter?</h2>
          <p className="text-muted-foreground mb-8">
            Join professionals who are getting more done with Arlo.
          </p>
          <Button size="lg" onClick={() => handleCTAClick('Get Started Free', 'footer_cta')} className="gap-2 bg-emerald-500 hover:bg-emerald-600">
            Get Started Free <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="relative inline-block">
              <div className="absolute -top-[5px] left-0">
                <div className="bg-white text-background px-[2px] py-[2px] rounded text-[6px] font-bold" style={{ fontFamily: "'Coco Gothic', sans-serif" }}>hey</div>
              </div>
              <span className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Coco Gothic', sans-serif" }}>arlo</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <button onClick={() => navigate('/pricing')} className="hover:text-foreground transition-colors">
                Pricing
              </button>
              <button onClick={() => navigate('/docs')} className="hover:text-foreground transition-colors">
                Docs
              </button>
              <button onClick={() => navigate('/terms')} className="hover:text-foreground transition-colors">
                Terms
              </button>
              <button onClick={() => navigate('/privacy')} className="hover:text-foreground transition-colors">
                Privacy
              </button>
              <span>© 2026 Arlo</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
